import { memo, useState } from "react";
import { Button, Card, CardActions, CardContent, Chip, Switch, TextField, Typography, Grid2 } from "@mui/material";
import { icon } from "@/app/style/icons";
import { SettingsFileList } from "@settings/components/forms/SettingsFileList";
import { useCompanyUploads } from "@/domains/services/hooks/useCompanyUploads";

interface JobSubmissionFormProps {
    title: string;
    description?: string;
    loading?: boolean;
    submitLabel?: string;
    onSubmit: (payload: {
        companyProfile: string;
        useExistingProfile: boolean;
        websites: string[];
        documents: string[];
    }) => void | Promise<void>;
    /**
     * Allows hiding the website chips input if not needed for a particular service.
     */
    showWebsitesInput?: boolean;
    /**
     * Allows hiding the existing-profile switch if not needed.
     */
    showExistingProfileSwitch?: boolean;
    /** Company ID to enable document upload section */
    companyId?: string;
    /** Show upload? default true if companyId present */
    showDocumentUpload?: boolean;
}

const JobSubmissionForm = memo(
    ({
        title,
        description,
        loading,
        submitLabel = "Absenden",
        onSubmit,
        showWebsitesInput = true,
        showExistingProfileSwitch = true,
        companyId,
        showDocumentUpload = true,
    }: JobSubmissionFormProps) => {
        const [companyProfile, setCompanyProfile] = useState("");
        const [useExistingProfile, setUseExistingProfile] = useState(false);
        const [websites, setWebsites] = useState<string[]>([]);
        const [websiteInput, setWebsiteInput] = useState("");

        // Dokument-Uploads
        const {
            documents,
            documentsLoading,
            uploading,
            deleting,
            handleFileSelect,
            handleDeleteDocument,
        } = useCompanyUploads(companyId || "");

        const handleSubmit = async () => {
            await onSubmit({ companyProfile, useExistingProfile, websites, documents: documents.map(d => d.filename) });
        };

        return (
            <Grid2 container spacing={3}>
                <Grid2 size={12}>
                    <Typography variant="h4">{title}</Typography>
                </Grid2>

                {description && (
                    <Grid2 size={12}>
                        <Typography variant="body1" color="text.secondary">
                            {description}
                        </Typography>
                    </Grid2>
                )}

                {/* Firmenprofil */}
                <Grid2 size={12}>
                    <Typography fontSize="0.95rem" fontWeight={500} sx={{ mb: 0.5 }}>
                        Dein Firmenprofil:
                    </Typography>
                    <TextField
                        placeholder="Hier könnte dein Firmenprofil stehen..."
                        sx={{ outline: "none" }}
                        multiline
                        minRows={10}
                        fullWidth
                        value={companyProfile}
                        onChange={(e) => setCompanyProfile(e.target.value)}
                    />
                    <Typography fontSize="0.7rem" align="right" sx={{ pr: 1, pt: 0.5 }}>
                        mind. 200 Zeichen
                    </Typography>
                </Grid2>

                {/* Dokument-Uploads */}
                {companyId && showDocumentUpload && (
                    <Grid2 size={12}>
                        <SettingsFileList
                            files={documents}
                            onDelete={handleDeleteDocument}
                            onUpload={handleFileSelect}
                            loading={documentsLoading}
                            uploadingInProgress={uploading || deleting}
                            label="Dokumente als Analysegrundlage"
                        />
                    </Grid2>
                )}

                {/* Optionen */}
                <Grid2 container size={12} spacing={3}>
                    {showExistingProfileSwitch && (
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Card sx={{ height: "100%" }}>
                                <CardContent>Bestehendes Firmenprofil verwenden:</CardContent>
                                <CardActions>
                                    <Switch
                                        checked={useExistingProfile}
                                        onChange={(e) => setUseExistingProfile(e.target.checked)}
                                    />
                                </CardActions>
                            </Card>
                        </Grid2>
                    )}

                    {showWebsitesInput && (
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Card sx={{ height: "100%" }}>
                                <CardContent sx={{ mb: -0.5 }}>
                                    <TextField
                                        label="Unternehmenswebseiten"
                                        fullWidth
                                        value={websiteInput}
                                        onChange={(e) => setWebsiteInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && websiteInput.trim()) {
                                                e.preventDefault();
                                                setWebsites((prev) => [...prev, websiteInput.trim()]);
                                                setWebsiteInput("");
                                            }
                                        }}
                                    />
                                </CardContent>
                                <CardActions sx={{ ml: 0.5 }}>
                                    {websites.map((site, idx) => (
                                        <Chip
                                            key={idx}
                                            label={site}
                                            clickable
                                            onDelete={() =>
                                                setWebsites((prev) => prev.filter((_, i) => i !== idx))
                                            }
                                        />
                                    ))}
                                </CardActions>
                            </Card>
                        </Grid2>
                    )}
                </Grid2>

                {/* Submit */}
                <Grid2 container size={12} mt={3} justifyContent="flex-end">
                    <Button
                        variant="contained"
                        startIcon={<icon.arrowRight />}
                        disabled={loading || uploading || deleting}
                        onClick={handleSubmit}
                    >
                        {loading ? "Wird eingereicht…" : submitLabel}
                    </Button>
                </Grid2>
            </Grid2>
        );
    },
);

export default JobSubmissionForm; 